import { 
  ShoppingCart, Globe, Bot, ArrowUpRight
} from "lucide-react";
import {
  Storefront, ChartLineUp, Swap, CreditCard, Repeat,
  Globe as PGlobe, Buildings, Translate, Gauge, Stack,
  Robot, Article, ChatCircleDots, Plugs, BookOpen,
} from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const services = [
  {
    icon: ShoppingCart,
    num: "01",
    title: "Ecommerce",
    description: "Launch and scale your online store on Shopify or WooCommerce. We build Kuwait-ready storefronts with KNET, Tap and MyFatoorah, conversion-led design and a checkout engineered to sell across the GCC.",
    items: [
      { label: "Store design & development", icon: Storefront },
      { label: "Growth & optimisation", icon: ChartLineUp },
      { label: "Migrations & integrations", icon: Swap },
      { label: "Subscription commerce", icon: Repeat },
    ],
    href: "/ecommerce",
  },
  {
    icon: Globe,
    num: "02",
    title: "Web",
    description: "Custom WordPress and Webflow websites designed and built in Kuwait. Fast, secure and SEO-ready sites — from corporate platforms to multilingual experiences your team can update without touching code.",
    items: [
      { label: "Webflow & CMS development", icon: PGlobe },
      { label: "Corporate sites & landing pages", icon: Buildings },
      { label: "Multilingual websites", icon: Translate },
      { label: "Performance optimisation", icon: Gauge },
    ],
    href: "/web",
  },
  {
    icon: Bot,
    num: "03",
    title: "AI",
    description: "Practical AI for Kuwait businesses — agents, automations and intelligent apps that cut cost, save time and serve customers around the clock. Real outcomes, not hype.",
    items: [
      { label: "AI automation & workflows", icon: Robot },
      { label: "Content generation", icon: Article },
      { label: "AI agents & chatbots", icon: ChatCircleDots },
      { label: "SaaS & knowledge integrations", icon: Plugs },
    ],
    href: "/ai",
  },
];

export function Services() {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  return (
    <section className="py-10 lg:py-24 relative bg-background">
      <div className="container px-6">
        {/* Section header — left-aligned like Charle */}
        <div className="max-w-2xl mb-7">
          <motion.p
            className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t("Delivering Growth")}
          </motion.p>
          <motion.h2
            className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.05] text-balance"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            {t("Our Services")}
          </motion.h2>
        </div>

        {/* Services list — vertical, numbered layout */}
        <div className="space-y-0 divide-y divide-border">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link
                to={localePath(service.href)}
                className="group flex flex-col gap-6 py-12 lg:py-16"
              >
                {/* Title row */}
                <div className="flex items-start gap-4">
                  <span className="text-sm font-medium text-muted-foreground/50 mt-1.5 tabular-nums">
                    {service.num}
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors duration-300">
                      <service.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-sans font-medium text-foreground group-hover:text-primary transition-colors duration-300">
                      {t(service.title)}
                    </h3>
                    <ArrowUpRight className="h-5 w-5 text-primary opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </div>
                </div>

                {/* Description + Tags side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
                  <p className="text-muted-foreground text-[0.95rem] leading-[1.7] text-balance">
                    {t(service.description)}
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-3">
                    {service.items.map((item) => (
                      <span key={item.label} className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-foreground/[0.04] text-[0.95rem] text-muted-foreground leading-[1.7]">
                        <item.icon size={16} className="text-primary/60 flex-shrink-0" />
                        {t(item.label)}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View all services link */}
        <motion.div
          className="mt-12 pt-8 border-t border-border"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Link
            to={localePath("/services")}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            {t("View all services")}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
